<template>
  <div class="donation-form">
    <!-- Payment frequency -->
    <div class="form-field">
      <h4 class="form-field-header" v-translate>Select payment frequency</h4>
      <div class="form-field-container">
        <CheckboxRadioButtons
          name="anonymous-donation-frequency-options"
          :options="Donation.options.FREQUENCY_CHOICES"
          :initial="defaultFrequencyOption"
          @selected="onChangeFrequency"
        />
      </div>
    </div>

    <!-- Amount -->
    <div class="form-field amount-field">
      <h4 class="form-field-header" v-translate>Amount</h4>
      <div class="form-field-container">
        <md-field class="amount-input form-field-text">
          <md-icon style="font-size: 18px !important">attach_money</md-icon>
          <md-input v-model="donation.amount" type="number" required></md-input>
        </md-field>
      </div>
    </div>

    <!-- Payment details -->
    <div class="form-field">
      <h3 class="payment-details-header" v-translate>Payment method</h3>
    </div>

    <!-- Card details -->
    <!--
    <div class="form-field">
      <h4 class="form-field-header" v-translate>Card details</h4>
      <div class="form-field-container">
        <PaymentFormFlat @change="onPaymentUpdate" />
      </div>
    </div>
    -->

    <div class="form-field payment-methods">
      <div v-if="paymentMethods">
        <h4 class="form-field-header" v-translate>Select payment method</h4>
        <PaymentMethods
          v-if="!paymentsLoading"
          :paymentMethods="paymentMethods"
          @selected="onChangePaymentMethod"
        />
        <div v-if="paymentsLoading" class="payments-loading-spinner">
          <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
        </div>
      </div>
      <!-- Add Payment Method -->
      <div class="form-field add-payment-content">
        <a v-if="!showAddPaymentMethod" @click="onAddPaymentMethod" v-translate
          >+ Add new payment method</a
        >

        <div v-show="showAddPaymentMethod" class="add-new-payment">
          <h4 class="form-field-header" v-translate>Card details</h4>
          <div class="form-field-container">
            <PaymentFormFlat @change="onPaymentUpdate" />
          </div>

          <div style="display: flex; align-items: center;">
            <md-button @click="onSavePaymentMethod" class="md-dense md-raised add-cc-button">
              <span v-if="!savingPaymentMethod" v-translate>Save</span>
              <LoadSpinner v-if="savingPaymentMethod" :color="'white'" />
            </md-button>
            <md-button
              class="md-dense md-raised"
              @click="showAddPaymentMethod = !showAddPaymentMethod"
            >
              <span v-translate class="inherit-font"> Cancel</span>
            </md-button>
          </div>
        </div>
      </div>
    </div>
    <!-- Add Payment Method End -->

    <!-- Submit -->
    <div class="button-container">
      <button @click="onNext" class="form-button">
        <span><translate>Next</translate></span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { stripe } from '@/services/stripe'
import LoadSpinner from '@/components/LoadSpinner'
import PaymentFormFlat from '@/components/donations/PaymentFormFlat'
import CheckboxRadioButtons from '@/components/CheckboxRadioButtons'
import PaymentMethod from '@/services/payment'
import PaymentMethods from '@/components/donations/PaymentMethods'
import Donation from '@/services/donations'

const cardBrandLookup = {
  'cc-visa': 'Visa',
  'cc-amex': 'American Express',
  'cc-discover': 'Discover',
  'cc-mastercard': 'MasterCard',
}

export default {
  name: 'DonationForm',
  components: {
    LoadSpinner,
    PaymentMethods,
    PaymentFormFlat,
    CheckboxRadioButtons,
  },
  data() {
    return {
      Donation,

      // Payment Methods
      paymentMethods: [],
      paymentsLoading: true,
      stripeCard: null,
      savingPaymentMethod: false,
      selectedPaymentMethod: {},
      showAddPaymentMethod: false,

      // Donations
      donation: Donation.create({
        amount: '',
        frequency: Donation.options.RECURRING,
        organization: this.$store.state.organizationDetails.id,
      }),
    }
  },
  async created() {
    // If there's already a current donation in state, repopulate from there
    if (this.currentDonation) {
      this.donation.amount = this.currentDonation.amount
      this.donation.frequency = this.currentDonation.frequency
    }

    await this.refreshPaymentMethods()
    this.paymentsLoading = false
  },
  methods: {
    async refreshPaymentMethods() {
      const paymentMethods = await PaymentMethod.api.list()
      if (paymentMethods.length > 0) {
        this.selectedPaymentMethod = paymentMethods[0]
      }
      this.paymentMethods = paymentMethods
    },
    cardBrandCheck(brand) {
      return cardBrandLookup[brand]
    },
    onAddPaymentMethod() {
      this.showAddPaymentMethod = !this.showAddPaymentMethod
      this.$emit('clicked-cta', 'add payment method')
    },
    onChangeFrequency(option) {
      this.donation.frequency = option.value
    },

    /**
     * Change the default payment method, whenever the user selects
     * one from the dropdown list. This ensures that when the donation
     * goes through, this payment method will be used.
     */
    async onChangePaymentMethod(card) {
      this.paymentsLoading = true
      try {
        await PaymentMethod.api.updateDefaultPaymentMethod(card)
        await this.refreshPaymentMethods()
      } finally {
        this.paymentsLoading = false
      }
    },
    async onSavePaymentMethod() {
      this.savingPaymentMethod = true
      try {
        const response = await stripe.createToken(this.stripeCard)
        const newCard = await PaymentMethod.api.addPaymentMethod(response.token.id)
        await this.onChangePaymentMethod(newCard)
        this.showAddPaymentMethod = false
      } finally {
        this.savingPaymentMethod = false
      }
    },
    onNext() {
      // Format donation appropriately and save to state
      const amount = Number(this.donation.amount).toFixed(2)
      this.donation.amount = amount
      this.donation.paymentType = this.selectedPaymentMethod.formatCardType()
      this.donation.paymentLast4 = this.selectedPaymentMethod.last4
      this.$store.dispatch('updateCurrentDonation', this.donation)

      // Route to review screen
      this.$router.push({ name: 'DonationReview' })
    },
    onPaymentUpdate(event) {
      this.stripeCard = event.stripeCard
    },
  },
  computed: {
    ...mapState(['organizationDetails', 'currentDonation']),
    ...mapGetters(['userIsLoggedIn']),
    /* Select frequency option to be selected by default. */
    defaultFrequencyOption() {
      return Donation.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.donation.frequency,
      )
    },
    /*
     * Disable donation if user has no payment methods, or user is currently adding a new payment method
     */
    disabled() {
      return (
        this.paymentMethods.length == 0 ||
        this.showAddPaymentMethod ||
        !this.donation.amount ||
        // Stripe doesn't accept donations less than 50 cents
        this.donation.amount < 0.5
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.md-field .md-input {
  border-bottom: 0;
}

@include tablet-and-up-screen {
  .amount-field,
  .payment-methods {
    max-width: 396px;
  }
}

.payment-details-header {
  color: black;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 2rem;
}

.amount-input {
  padding: 0.75rem;
}

.donation-form {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.payment {
  width: 100%;
  height: 500px;
}
h5 {
  color: $base-gray;
}
.box {
  width: 100%;
  height: 30px;
  font-size: 18px;
  color: $base-gray;
  border-bottom: 1px solid $mid-gray;
  display: flex;
  padding: 4px;
}
.dropdown-select {
  width: 100%;
  border-top: none;
  background: $very-light-gray;
  box-shadow: 0 0.25rem 0.5rem 0 $mid-gray;
  border-bottom-left-radius: 0.6rem;
  border-bottom-right-radius: 0.6rem;
  border: 1px, solid $mid-gray;
  background-color: white;
  overflow-y: auto;
  max-height: 30vh;

  & > li {
    margin-left: 0;
  }
}
.button {
  @include primary-button(var(--dark-brand-color));
}
.add-cc-button {
  background-color: var(--dark-brand-color);
  color: $white;
}
.exp-date {
  color: $base-gray;
}
.exp-date-selected {
  font-size: 0.9rem;
  margin-left: 5px;
  font-family: $base-h4-font;
}
.payment-list-item {
  width: 100%;
  padding: 1rem;
  border-bottom: 1px solid $mid-gray;
  font-size: 16px;
  cursor: pointer;
}
.add-payment-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 2rem;
}
.add-payment-button {
  cursor: pointer;
  padding-left: 1.5rem;
  color: var(--dark-brand-color);
}
.last4 {
  margin: 0;
}
.select-payment {
  margin-right: 10px;
  font-size: 12px;
  font-family: $base-h4-font;
  color: $soft-gray;
}
.selected-payment-container {
  display: flex;
  justify-content: space-evenly;
}
.payment-selected {
  display: flex;
  padding-bottom: 18px;
}
.payment-selected-icon {
  font-size: 20px;
  margin-right: 8px;
  align-self: baseline;
}
.payment-details {
  width: 100%;
}
.disabled-cc-input {
  pointer-events: none;
  opacity: 0.4;
}
.disabled-button {
  @include primary-button-disabled();
}
.subheading {
  text-align: center;
  margin-top: -30px;
}
.section {
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
}
.add-new-payment {
  width: 100%;
}
.collapsed {
  background: $white;
  border: 2px solid var(--dark-brand-color);
}
.collapsed-text {
  color: var(--dark-brand-color);
}
.toFAQ {
  font-size: 0.7rem;
  font-weight: bold;
  color: var(--dark-brand-color);
}
.payments-loading-spinner {
  // Same height as PaymentMethods component
  height: 5rem;
}
</style>
